import { Fragment } from "react";

import CommunityScoreWheel, {
  type CommunityScorePercentageType,
} from "@/components/community-score-wheel";
import { Separator } from "@/components/ui/separator";
import { toSentenceCase } from "@/helpers/formatters";
import { cn } from "@/lib/utils";

import { InfoIcon } from "./icons";

const TXT_CLASS_NAME = cn(
  "text-14 text-white leading-[1.57em] md:leading-[1.71em]"
);

interface RowProps {
  label: string;
  value: string;
}

export function Row({ label, value }: RowProps) {
  return (
    <dl className="my-auto flex items-center justify-between pt-2.5 pb-[5px] md:pb-[3px]">
      <dt className={cn(TXT_CLASS_NAME, "font-medium md:font-normal")}>
        {label}
      </dt>
      <dd className={TXT_CLASS_NAME}>{toSentenceCase(value)}</dd>
    </dl>
  );
}

export interface RatingType {
  label: string;
  value: string;
}

export interface RatingsProps {
  communityRatingCount?: number;
  communityScoreBucket?: string;
  communityScorePercentages?: CommunityScorePercentageType[];
  components: RatingType[];
}

export default function Ratings({
  communityRatingCount,
  communityScoreBucket,
  communityScorePercentages,
  components,
}: RatingsProps) {
  if (!(communityRatingCount || components.length)) {
    return null;
  }

  return (
    <div className="pb-5">
      <div className="mb-6.5 flex items-center gap-2.5 md:mb-6">
        <h3 className="text-20 text-white leading-[1.2em] md:text-24">
          Ratings
        </h3>
        <a
          aria-label="All about Morty ratings"
          href="https://link.morty.app/e/tgWEluAIrsb"
          rel="nofollow noopener"
          target="_blank"
        >
          <InfoIcon />
        </a>
      </div>
      {!!communityRatingCount && (
        <div className="mb-7.5 flex h-15 items-center justify-between rounded-[15px] bg-deep-cove-dark px-[11px] md:px-5">
          <p className="whitespace-nowrap font-semibold text-14 text-white md:text-16">
            All{" "}
            {communityScoreBucket && communityScoreBucket.length < 20
              ? "ratings"
              : ""}{" "}
            <span>
              ({communityRatingCount < 3 ? "<3" : communityRatingCount})
            </span>
          </p>
          <div className="flex items-center gap-[7px] md:gap-2.5">
            {!!communityScoreBucket && (
              <p className="text-right font-semibold text-14 text-white md:text-16">
                {toSentenceCase(communityScoreBucket)}
              </p>
            )}
            {communityScorePercentages && (
              <CommunityScoreWheel
                className="size-[21px]"
                scores={communityScorePercentages}
              />
            )}
          </div>
        </div>
      )}
      {!!components.length && (
        <dl className="flex flex-col items-stretch rounded-[15px] bg-white/10 px-[11px] pt-1.5 pb-[13px] md:px-5">
          {components.map(({ label, value }, index) => (
            <Fragment key={label}>
              {!!index && <Separator className="bg-white/8" />}
              <Row label={label} value={value} />
            </Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}
